import { httpsCallable } from "firebase/functions";
import { functions } from "../firebaseConfig";

// Constants
const GMAIL_CLIENT_ID = "406079058578-2ic452har5d3evviadiklp8igl2pir43.apps.googleusercontent.com";
const REDIRECT_URI = "https://crm.scholarcred.com/integrations/gmail";
const TOKEN_STORAGE_KEY = "gmailTokens";
const TOKEN_EXPIRY_BUFFER = 300; // 5 minutes buffer before actual expiration

// Types
interface GmailTokens {
  access_token: string;
  refresh_token: string | null;
  expiry_date?: number; // Timestamp when the token expires
}

export interface EmailRecipient {
  email: string;
  name?: string;
}

export interface EmailAttachment {
  filename: string;
  content: string; // Base64 encoded content
  mimeType: string;
}

interface EmailOptions {
  to: EmailRecipient[];
  cc?: EmailRecipient[];
  bcc?: EmailRecipient[];
  subject: string;
  body: string;
  isHtml?: boolean;
  attachments?: EmailAttachment[];
}

// Firebase function references
const getAccessTokenFn = httpsCallable(functions, 'getGmailAccessToken');
const refreshTokenFn = httpsCallable(functions, 'getGmailAccessTokenFromRefreshToken');

/**
 * Initiate OAuth2 authentication flow
 */
export const initiateGmailAuth = () => {
  const authUrl = `https://accounts.google.com/o/oauth2/auth?client_id=${GMAIL_CLIENT_ID}&redirect_uri=${encodeURIComponent(
    REDIRECT_URI
  )}&response_type=code&scope=${encodeURIComponent(
    'https://www.googleapis.com/auth/gmail.send'
  )}&access_type=offline&prompt=consent`;

  window.location.href = authUrl;
};

/**
 * Handle OAuth2 callback - exchange code for tokens
 * @param code - Authorization code from Google
 */
export const handleGmailAuthCallback = async (code: string): Promise<boolean> => {
  try {
    const result = await getAccessTokenFn({ code });
    const data = result.data as any;

    if (data.error) {
      console.error('Error getting access token:', data.error);
      return false;
    }

    if (data.access_token) {
      // Calculate expiry (access tokens typically last 1 hour)
      const expiryDate = Date.now() + 3600 * 1000; // 1 hour from now
      
      // Store tokens in localStorage
      const tokens: GmailTokens = {
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expiry_date: expiryDate
      };
      
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error in Gmail auth callback:', error);
    return false;
  }
};

/**
 * Check if Gmail tokens exist and are valid
 */
export const hasValidGmailToken = (): boolean => {
  try {
    const tokensStr = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!tokensStr) return false;
    
    const tokens: GmailTokens = JSON.parse(tokensStr);
    if (!tokens.access_token) return false;
    
    // Check if token is expired or will expire soon
    if (tokens.expiry_date) {
      return Date.now() < tokens.expiry_date - TOKEN_EXPIRY_BUFFER * 1000;
    }
    
    return true; // If no expiry date is set, assume token is valid
  } catch (error) {
    console.error('Error checking Gmail token validity:', error);
    return false;
  }
};

/**
 * Get a valid access token, refreshing if necessary
 */
export const getValidGmailToken = async (): Promise<string | null> => {
  try {
    const tokensStr = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!tokensStr) return null;
    
    const tokens: GmailTokens = JSON.parse(tokensStr);
    
    // Check if token is expired or will expire soon
    if (!tokens.expiry_date || Date.now() >= tokens.expiry_date - TOKEN_EXPIRY_BUFFER * 1000) {
      // Token is expired or will expire soon, refresh it
      if (!tokens.refresh_token) {
        console.error('No refresh token available');
        return null;
      }
      
      const refreshed = await refreshGmailToken(tokens.refresh_token);
      if (refreshed) {
        const newTokensStr = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (!newTokensStr) return null;
        
        const newTokens: GmailTokens = JSON.parse(newTokensStr);
        return newTokens.access_token;
      }
      
      return null;
    }
    
    return tokens.access_token;
  } catch (error) {
    console.error('Error getting valid Gmail token:', error);
    return null;
  }
};

/**
 * Refresh the Gmail access token
 */
export const refreshGmailToken = async (refreshToken: string): Promise<boolean> => {
  try {
    const result = await refreshTokenFn({ refresh_token: refreshToken });
    const data = result.data as any;
    
    if (data.error) {
      console.error('Error refreshing token:', data.error);
      return false;
    }
    
    if (data.access_token) {
      // Calculate new expiry
      const expiryDate = Date.now() + 3600 * 1000; // 1 hour from now
      
      // Update tokens in localStorage
      const tokens: GmailTokens = {
        access_token: data.access_token,
        refresh_token: data.refresh_token || refreshToken, // Keep old refresh token if not provided
        expiry_date: expiryDate
      };
      
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error refreshing Gmail token:', error);
    return false;
  }
};

/**
 * Clear stored Gmail tokens
 */
export const clearGmailTokens = (): void => {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
};

/**
 * Base64 encode a string (for email)
 */
const base64UrlEncode = (str: string): string => {
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

/**
 * Send an email via Gmail API
 */
// export const sendGmailEmail = async (options: EmailOptions): Promise<boolean> => {
//   try {
//     // Get a valid token first
//     const accessToken = await getValidGmailToken();
    
//     if (!accessToken) {
//       console.error('No valid Gmail access token available');
//       // Initiate auth flow if no valid token
//       initiateGmailAuth();
//       return false;
//     }
    
//     // Format recipients
//     const formatRecipient = (recipient: EmailRecipient): string => {
//       return recipient.name 
//         ? `${recipient.name} <${recipient.email}>`
//         : recipient.email;
//     };
    
//     const toField = options.to.map(formatRecipient).join(',');
//     const ccField = options.cc?.map(formatRecipient).join(',') || '';
//     const bccField = options.bcc?.map(formatRecipient).join(',') || '';
    
//     // Create email content
//     let emailContent = [
//       `To: ${toField}`,
//       ccField ? `Cc: ${ccField}` : '',
//       bccField ? `Bcc: ${bccField}` : '',
//       `Subject: ${options.subject}`,
//       'Content-Type: ' + (options.isHtml ? 'text/html' : 'text/plain') + '; charset=utf-8',
//       '',
//       options.body
//     ].filter(Boolean).join('\r\n');
    
//     // Add attachments if any
//     if (options.attachments && options.attachments.length > 0) {
//       const boundary = `boundary_${Date.now().toString(16)}`;
      
//       // Redefine email with multipart content
//       emailContent = [
//         `To: ${toField}`,
//         ccField ? `Cc: ${ccField}` : '',
//         bccField ? `Bcc: ${bccField}` : '',
//         `Subject: ${options.subject}`,
//         `Content-Type: multipart/mixed; boundary=${boundary}`,
//         '',
//         `--${boundary}`,
//         'Content-Type: ' + (options.isHtml ? 'text/html' : 'text/plain') + '; charset=utf-8',
//         'Content-Transfer-Encoding: 7bit',
//         '',
//         options.body,
//         ''
//       ].filter(Boolean).join('\r\n');
      
//       // Add each attachment
//       for (const attachment of options.attachments) {
//         const attachmentPart = [
//           `--${boundary}`,
//           `Content-Type: ${attachment.mimeType}`,
//           'Content-Transfer-Encoding: base64',
//           `Content-Disposition: attachment; filename="${attachment.filename}"`,
//           '',
//           attachment.content.replace(/(.{76})/g, "$1\r\n"),
//           ''
//         ].join('\r\n');
        
//         emailContent += attachmentPart;
//       }
      
//       emailContent += `--${boundary}--`;
//     }
    
//     // Encode the email
//     const encodedEmail = base64UrlEncode(emailContent);
    
//     // Send the email using Gmail API directly
//     const response = await fetch('https://www.googleapis.com/gmail/v1/users/me/messages/send', {
//       method: 'POST',
//       headers: {
//         Authorization: `Bearer ${accessToken}`,
//         'Content-Type': 'application/json'
//       },
//       body: JSON.stringify({
//         raw: encodedEmail
//       })
//     });
    
//     if (!response.ok) {
//       const errorData = await response.json();
//       console.error('Error sending email:', errorData);
      
//       // If unauthorized, token might be invalid despite our checks
//       if (response.status === 401) {
//         // Clear tokens and initiate auth flow
//         clearGmailTokens();
//         initiateGmailAuth();
//       }
      
//       return false;
//     }
    
//     return true;
//   } catch (error) {
//     console.error('Error sending Gmail email:', error);
//     return false;
//   }
// };

export const sendGmailEmail = async (options: EmailOptions): Promise<boolean> => {
  try {
    // Get a valid token first
    const accessToken = await getValidGmailToken();
    
    if (!accessToken) {
      console.error('No valid Gmail access token available');
      // Initiate auth flow if no valid token
      initiateGmailAuth();
      return false;
    }
    
    // Format recipients
    const formatRecipient = (recipient: EmailRecipient): string => {
      return recipient.name 
        ? `${recipient.name} <${recipient.email}>`
        : recipient.email;
    };
    
    const toField = options.to.map(formatRecipient).join(',');
    const ccField = options.cc?.map(formatRecipient).join(',') || '';
    const bccField = options.bcc?.map(formatRecipient).join(',') || '';
    
    // Generate a unique boundary string
    const boundary = `----MultipartBoundary_${Math.random().toString(36).substring(2)}`;
    
    // Create headers
    const headers = [
      `To: ${toField}`,
      ccField ? `Cc: ${ccField}` : '',
      bccField ? `Bcc: ${bccField}` : '',
      `Subject: ${options.subject}`,
      `MIME-Version: 1.0`
    ].filter(Boolean);
    
    let emailContent = '';
    
    // Add multipart container if we have attachments
    if (options.attachments && options.attachments.length > 0) {
      // Add multipart content type header
      headers.push(`Content-Type: multipart/mixed; boundary="${boundary}"`);
      emailContent = headers.join('\r\n') + '\r\n\r\n';
      
      // Add message body part
      emailContent += [
        `--${boundary}`,
        `Content-Type: ${options.isHtml ? 'text/html' : 'text/plain'}; charset=utf-8`,
        'Content-Transfer-Encoding: 7bit',
        '',
        options.body,
        ''
      ].join('\r\n');
      
      // Add each attachment
      for (const attachment of options.attachments) {
        // Ensure the content is properly formatted base64
        // Remove any existing line breaks or spaces to normalize
        let cleanBase64 = attachment.content.replace(/[\r\n\s]/g, '');
        
        // Chunk the content into lines of 76 characters
        let formattedContent = '';
        for (let i = 0; i < cleanBase64.length; i += 76) {
          formattedContent += cleanBase64.substring(i, i + 76) + '\r\n';
        }
        
        emailContent += [
          `--${boundary}`,
          `Content-Type: ${attachment.mimeType}`,
          'Content-Transfer-Encoding: base64',
          `Content-Disposition: attachment; filename="${encodeURIComponent(attachment.filename).replace(/%20/g, ' ')}"`,
          '',
          formattedContent,
          ''
        ].join('\r\n');
      }
      
      // Close the multipart message
      emailContent += `--${boundary}--\r\n`;
    } else {
      // Simple email without attachments
      headers.push(`Content-Type: ${options.isHtml ? 'text/html' : 'text/plain'}; charset=utf-8`);
      emailContent = headers.join('\r\n') + '\r\n\r\n' + options.body;
    }
    
    // Encode the email ensuring URL-safe base64
    const encodedEmail = base64UrlEncode(emailContent);
    
    // Send the email using Gmail API directly
    const response = await fetch('https://www.googleapis.com/gmail/v1/users/me/messages/send', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        raw: encodedEmail
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error sending email:', errorData);
      
      // If unauthorized, token might be invalid despite our checks
      if (response.status === 401) {
        // Clear tokens and initiate auth flow
        clearGmailTokens();
        initiateGmailAuth();
      }
      
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error sending Gmail email:', error);
    return false;
  }
};

/**
 * Check if user has authorized Gmail integration
 */
export const isGmailAuthorized = (): boolean => {
  return hasValidGmailToken();
};
